import { z } from 'zod';
import { DISMISSAL_REASONS } from './guidances.dto.js';
import { guidanceDto } from './journeys.dto.js';

export const RECOMMENDATION_KINDS = ['guidance', 'platform_change'] as const;
export type RecommendationKind = (typeof RECOMMENDATION_KINDS)[number];

export const RECOMMENDATION_STATES = ['pending', 'accepted', 'adjusted', 'dismissed'] as const;
export type RecommendationState = (typeof RECOMMENDATION_STATES)[number];

/** Site defects a platform recommendation may name (functional §5.6, closed list; mirror of the domain). */
export const PLATFORM_CAUSES = [
  'missing_field_validation',
  'unclear_label',
  'missing_document_list',
  'technical_dead_end',
] as const;
export type PlatformCause = (typeof PLATFORM_CAUSES)[number];

/** The figures that justify a proposal (functional §5.6 "motif chiffré"): aggregates only. */
export const frictionEvidenceDto = z.object({
  journeyName: z.string(),
  stepName: z.string(),
  pagePath: z.string(),
  fieldKey: z.string().nullable(),
  fieldLabel: z.string().nullable(),
  fieldType: z.string().nullable(),
  frictionScore: z.number().int().min(0).max(100),
  affectedSessions: z.number().int().min(0),
  hesitationCount: z.number().int().min(0),
  rageClickCount: z.number().int().min(0),
  errorCount: z.number().int().min(0),
  abandonCount: z.number().int().min(0),
});
export type FrictionEvidenceDto = z.infer<typeof frictionEvidenceDto>;

export const recommendationDecisionDto = z.object({
  decidedBy: z.string().uuid(),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().datetime(),
  outcome: z.enum(['accepted', 'adjusted', 'dismissed']),
  dismissalReason: z.enum(DISMISSAL_REASONS).nullable(),
  note: z.string().nullable(),
});
export type RecommendationDecisionDto = z.infer<typeof recommendationDecisionDto>;

/** One line of the validation queue (functional §5.6), with the guidance it produced when it did. */
export const recommendationDto = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  journeyId: z.string().uuid(),
  journeyName: z.string(),
  stepId: z.string().uuid(),
  stepName: z.string(),
  pagePathPattern: z.string(),
  kind: z.enum(RECOMMENDATION_KINDS),
  state: z.enum(RECOMMENDATION_STATES),
  evidence: frictionEvidenceDto,
  rationale: z.string(),
  expectedImpact: z.string(),
  /** Degraded mode (functional §5.6): produced without written text, to be completed before publication. */
  contentGenerationSkipped: z.boolean(),
  platformCause: z.enum(PLATFORM_CAUSES).nullable(),
  priorityScore: z.number().int().min(0).max(100),
  guidance: guidanceDto.nullable(),
  decision: recommendationDecisionDto.nullable(),
  createdAt: z.string().datetime(),
});
export type RecommendationDto = z.infer<typeof recommendationDto>;

export const MAX_PAGE_SIZE = 200;

/** One query parameter per criterion, paginated (technical §11.2). */
export const recommendationFiltersSchema = z
  .object({
    state: z.enum(RECOMMENDATION_STATES).optional(),
    kind: z.enum(RECOMMENDATION_KINDS).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(50),
  })
  .strict();
export type RecommendationFilters = z.infer<typeof recommendationFiltersSchema>;

export const recommendationPageDto = z.object({
  items: z.array(recommendationDto),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  totalCount: z.number().int().min(0),
  /** Pending proposals of the project, whatever the filters: the queue badge. */
  pendingCount: z.number().int().min(0),
});
export type RecommendationPage = z.infer<typeof recommendationPageDto>;

/** `POST /recommendations/{id}/decision` (technical §11.2): accept, or dismiss with a reason. */
export const decideRecommendationDto = z
  .object({
    decision: z.enum(['accept', 'dismiss']),
    reason: z.enum(DISMISSAL_REASONS).optional(),
    note: z.string().max(500).optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.decision === 'dismiss' && !value.reason) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: 'A dismissal requires a reason',
      });
    }
  });
export type DecideRecommendation = z.infer<typeof decideRecommendationDto>;

export const recommendationDecisionResultDto = z.object({
  id: z.string().uuid(),
  state: z.enum(RECOMMENDATION_STATES),
  guidanceId: z.string().uuid().nullable(),
});
export type RecommendationDecisionResult = z.infer<typeof recommendationDecisionResultDto>;

/** What leaves the product about recommendations (functional §5.6: platform changes handed to the client). */
export const RECOMMENDATION_EXPORT_DATASETS = ['platform-changes', 'decisions'] as const;
export type RecommendationExportDataset = (typeof RECOMMENDATION_EXPORT_DATASETS)[number];
export const recommendationExportDatasetSchema = z.enum(RECOMMENDATION_EXPORT_DATASETS);
